import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  effect,
  untracked,
  signal,
  input,
  inject,
  CUSTOM_ELEMENTS_SCHEMA,
  computed,
  ChangeDetectionStrategy,
  Inject,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { NgtCanvas, NgtState, extend, injectStore } from 'angular-three';
import * as THREE from 'three';
import { OrbitControls } from 'three-stdlib';
import { GamePluginRegistry } from '../services/game-plugin-registry.service';
import { ArService, ArOptions } from '../services/ar.service';
import { GameRenderer, GameResult } from '../interfaces/game-type-plugin.interface';
import { WheelGameSettings } from '@kanek/shared-data-access';
import {
  GameReadyEvent,
  GameStartEvent,
  GameCompleteEvent,
  GameErrorEvent,
} from '../interfaces/game-lifecycle-events.interface';

// CRITICAL: Register Three.js classes at module level BEFORE any Angular Three usage
// This must run before NgtCanvas tries to process ngt-* elements in child components
extend(THREE);

/**
 * Test 1: Simple Scene Graph Component
 * Match playground exactly including selector
 */
@Component({
  standalone: true,
  selector: 'app-simple-scene-graph',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ngt-mesh>
      <ngt-box-geometry />
      <ngt-mesh-basic-material color="hotpink" />
    </ngt-mesh>
  `
})
export class SimpleSceneGraph {}

/**
 * Game Engine Component - Phase D Architecture
 *
 * Declarative 3D game engine using Angular Three for reactive rendering.
 * Replaces imperative Three.js manipulation with component-based scene composition.
 *
 * **Phase D Architecture**:
 * - ✅ **Declarative**: Uses Angular components with template syntax
 * - ✅ **Reactive**: Responds to signal/input changes automatically
 * - ✅ **Type-safe**: Full TypeScript support with proper type checking
 * - ✅ **Maintainable**: Clear separation of concerns, easier to debug
 *
 * **How It Works**:
 * 1. Retrieves game plugin by `gameTypeId` (e.g., 'wheel', 'scratchcard')
 * 2. Calls `plugin.createRenderer().getSceneComponent()` to get scene component class
 * 3. Dynamically renders scene component via `[sceneGraph]` binding
 * 4. Scene component receives reactive config via signals/inputs
 * 5. All 3D updates happen automatically through Angular change detection
 *
 * **Portal Usage** (Preview mode with camera controls):
 * ```html
 * <lib-game-engine
 *   gameTypeId="wheel"
 *   [config]="{ settings: wheelSettings, items: prizes }"
 *   [enableCameraControls]="true"
 * />
 * ```
 *
 * **Experience App Usage** (AR mode - Phase 4 migration pending):
 * ```html
 * <lib-game-engine
 *   gameTypeId="wheel"
 *   [config]="{ settings: wheelSettings, items: prizes }"
 *   [enableAR]="true"
 * />
 * ```
 *
 * **Supported Game Types**:
 * - `wheel`: WheelSceneComponent (cylinder + arrow + HTML overlay)
 * - `scratchcard`: Future - needs Phase D migration
 *
 * @see WheelSceneComponent - Declarative wheel rendering
 * @see docs/WHEEL_GAME_MIGRATION_STATUS.md - Migration roadmap
 */
@Component({
  selector: 'lib-game-engine',
  standalone: true,
  imports: [CommonModule, NgtCanvas, MatButtonModule],
  templateUrl: './game-engine.component.html',
  styleUrl: './game-engine.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class GameEngineComponent implements OnInit, OnDestroy {
  // Expose test scene component class for sceneGraph binding (fallback)
  protected readonly SimpleSceneGraph = SimpleSceneGraph;

  /**
   * The game type plugin ID (e.g., 'wheel', 'scratchcard')
   */
  @Input({ required: true }) gameTypeId!: string;

  /**
   * The game-specific configuration object
   */
  protected configSignal = signal<unknown>(null);
  @Input() set config(value: unknown) {
    this.configSignal.set(value);
  }

  /**
   * Enable 3D camera controls (for Portal preview mode)
   */
  @Input() enableCameraControls = false;

  /**
   * Enable AR mode (for Experience app)
   * Using signal input for proper reactivity in effects
   */
  enableAR = input<boolean>(false);

  /**
   * AR marker pattern URL (required if enableAR = true)
   * Using signal input for proper reactivity in effects
   */
  arMarkerPattern = input<string | undefined>(undefined);

  /**
   * AR configuration options (patternRatio, smoothCount, etc.)
   * If not provided, defaults will be used
   * Using signal input for proper reactivity in effects
   */
  arOptions = input<Partial<ArOptions> | undefined>(undefined);

  /**
   * Optional AR video container element
   * If provided, AR video will be positioned within this container
   * If not provided, AR video will use default positioning
   */
  @Input() arVideoContainer?: HTMLElement;

  /**
   * Platform-provided callback to get winning prize index from Prize API.
   * Called by game renderers when they need to determine the winning prize.
   *
   * Example (Experience app with Firebase):
   * ```typescript
   * getPrizeIndex: async () => {
   *   const session = this.appStore.session();
   *   return await this.prizeApi.getWinningItem(session.id, session.short);
   * }
   * ```
   *
   * Example (Portal preview mode):
   * ```typescript
   * getPrizeIndex: async () => Math.floor(Math.random() * prizes.length)
   * ```
   */
  @Input() getPrizeIndex?: () => Promise<number>;

  /**
   * Optional AppStore for Experience app (used by scene components to set prizeRevealed flag)
   * Portal preview mode doesn't need this
   */
  @Input() appStore?: any;

  /**
   * Optional CTA button configuration
   * Games can provide their own interaction triggers
   */
  @Input() ctaButton?: {
    enabled: boolean;
    text: string;
    backgroundColor?: string;
    textColor?: string;
    position?: { bottom: string };
  };

  /**
   * Initial camera position for restoring saved camera state
   * Used by preview component to avoid flash on load
   */
  @Input() initialCameraPosition?: { x: number; y: number; z: number };

  /**
   * Preview mode flag.
   * When true, exposes `resetGame()` so the preview UI can reset game state
   * without destroying and recreating this component.
   *
   * Guards `resetGame()` against accidental calls in the Experience app
   * (the Experience app never shows a Refresh button to users, so this is
   * naturally unreachable in production even without the guard).
   */
  @Input() previewMode = false;

  /**
   * Game lifecycle event outputs
   */
  @Output() gameReady = new EventEmitter<GameReadyEvent>();
  @Output() gameStart = new EventEmitter<GameStartEvent>();
  @Output() gameComplete = new EventEmitter<GameCompleteEvent>();
  @Output() gameError = new EventEmitter<GameErrorEvent>();

  // Internal state signals
  private rendererSignal = signal<GameRenderer | null>(null);
  protected showCTA = signal(false);
  protected ctaDisabled = signal(false); // Disable button during Prize API call and animations
  /** Overrides ctaButton.text when set; null falls back to the static ctaButton label. */
  protected ctaLabel = signal<string | null>(null);
  private canvasStoreSignal = signal<NgtState | null>(null);
  private arInitializedSignal = signal(false);

  // Version signal to trigger scene component updates when userData changes
  // Scene components read this to know when to re-read userData
  private userDataVersionSignal = signal(0);

  private initialized = false;
  private pluginRegistry = inject(GamePluginRegistry);
  private arService = inject(ArService, { optional: true });
  private document = inject(DOCUMENT);
  private elementRef = inject(ElementRef);

  // Store reference to OrbitControls (set by scene components)
  private orbitControlsInstance: OrbitControls | null = null;

  // ResizeObserver to detect container changes
  private resizeObserver: ResizeObserver | null = null;

  // Computed signal to get scene component from renderer
  protected sceneComponent = computed(() => {
    const renderer = this.rendererSignal();
    const config = this.configSignal(); // Track config changes to force scene recreation
    const _ = this.userDataVersionSignal(); // Also track userDataVersion

    if (!renderer || !renderer.getSceneComponent) {
      // Use fallback test component if renderer not ready
      return SimpleSceneGraph;
    }
    // Get scene component from renderer (e.g., WheelSceneComponent)
    // When config changes, this computed re-runs, Angular Three sees a "new" value and recreates the scene
    return renderer.getSceneComponent();
  });

  // Expose userDataVersion for template @if to force canvas recreation
  protected configVersion = computed(() => this.userDataVersionSignal());

  // Camera configuration
  protected cameraConfig = computed(() => {
    const initial = this.initialCameraPosition;
    return {
      fov: 55,
      position: initial ? [initial.x, initial.y, initial.z] as [number, number, number] : [0, 0, 5] as [number, number, number],
      near: 0.1,
      far: 1000
    };
  });

  // Scene configuration
  protected sceneConfig = computed(() => ({
    background: new THREE.Color('#f0f0f0')
  }));

  constructor() {
    // Initialize AR if enabled
    this.initializeAR();

    // Store configSignal in scene.userData so scene components can read and track it
    effect(() => {
      const canvasStore = this.canvasStoreSignal();
      const enableARValue = this.enableAR();  // Read signal input

      // Store the SIGNAL itself, not the value - scene components will call it to track changes
      if (canvasStore?.scene) {
        canvasStore.scene.userData = {
          ...canvasStore.scene.userData, // Preserve existing properties (e.g., orbitControls)
          configSignal: this.configSignal, // Store signal function - scene components call this
          showCTA: this.showCTA, // Store showCTA signal for game reset
          /** Dynamically override the CTA button label from a renderer. Pass null to revert to the static ctaButton.text. */
          setCTALabel: (label: string | null) => this.ctaLabel.set(label),
          enableCameraControls: this.enableCameraControls,
          enableAR: enableARValue,
          appStore: this.appStore, // Pass AppStore to scene components
          triggerPlay: () => this.play(), // Provide play() function for auto-trigger
          renderer: this.rendererSignal(), // Pass renderer instance to scene (for scratchcard)
          _expectsPrizeApi: !!this.getPrizeIndex // Flag: true if Prize API is expected (Experience App)
        };

        console.log('[GameEngine] Updated scene.userData with renderer');
      }
    });

    // For scratchcard: Call Prize API and configure renderer AFTER it's in scene.userData
    // This ensures scene component finds fully-configured renderer with winning prize
    effect(() => {
      const renderer = this.rendererSignal();
      const config = this.configSignal();
      const canvasStore = this.canvasStoreSignal();

      // Only run for scratchcard when canvas is ready and renderer exists
      if (this.gameTypeId === 'scratchcard' && renderer && config && canvasStore?.scene && !this.initialized) {
        // Call Prize API to get winning prize index
        if (this.getPrizeIndex) {
          this.getPrizeIndex().then((prizeIndex) => {
            const campaignPrizes = this.appStore?.campaign()?.prizes || [];
            const winningPrize = campaignPrizes[prizeIndex];

            if (winningPrize) {
              console.log('[GameEngine] Prize API:', winningPrize.name);

              // Configure renderer with winning prize (renderer will store it but defer grid creation)
              const baseConfig = (config || {}) as Record<string, unknown>;
              const configWithWinner = { ...baseConfig, winningPrize };
              Promise.resolve(renderer.updateConfig(configWithWinner)).then(() => {
                this.initialized = true;
                this.gameReady.emit({});

                // Update configSignal to trigger scene component's effect
                this.configSignal.set({ ...configWithWinner, _prizeApiComplete: true });

                // Show CTA button if configured
                if (this.ctaButton?.enabled) {
                  this.showCTA.set(true);
                }
              });
            }
          }).catch((error) => {
            console.error('[GameEngine] Failed to fetch winning prize:', error);
          });
        } else {
          // No Prize API callback - configure with random prize (preview mode)
          console.log('[GameEngine] No Prize API callback - using preview mode');
          Promise.resolve(renderer.updateConfig(config)).then(() => {
            this.initialized = true;
            this.gameReady.emit({});

            if (this.ctaButton?.enabled) {
              this.showCTA.set(true);
            }
          });
        }
      }
      // For other games (wheel, gameboard), configure immediately
      else if (this.gameTypeId !== 'scratchcard' && renderer && config && !this.initialized) {
        Promise.resolve(renderer.updateConfig(config)).then(() => {
          console.log('[GameEngine] Renderer configured');
          this.initialized = true;
          this.gameReady.emit({});

          if (this.ctaButton?.enabled) {
            this.showCTA.set(true);
          }
        });
      }
    });

    // Update renderer when config changes
    effect(() => {
      const config = this.configSignal();
      const renderer = this.rendererSignal();

      if (renderer && config && this.initialized) {
        Promise.resolve(renderer.updateConfig(config)).then(() => {
          console.log('[GameEngine] Renderer config updated');

          // Also update 3D meshes in scene if they exist
          const canvasStore = this.canvasStoreSignal();
          if (canvasStore?.scene) {
            this.updateSceneMaterials(canvasStore.scene, config);
          }
        }).catch((err: unknown) => {
          console.error('[GameEngine] Error updating renderer config:', err);
        });
      }
    });

    // Subscribe to render loop for AR marker detection
    effect(() => {
      const canvasStore = this.canvasStoreSignal();
      const arInitialized = this.arInitializedSignal();

      if (canvasStore && arInitialized && this.arService) {
        // Subscribe to Angular Three's render loop
        const subscription = canvasStore.internal.subscribe(() => {
          // This runs on every frame - call AR marker detection
          this.arService?.injectBeforeRender();
        });

        // Cleanup subscription when effect re-runs or component destroys
        return () => {
          subscription?.();
        };
      }

      return undefined;
    });
  }

  /**
   * Called when NgtCanvas is created - gives us access to the store
   */
  protected onCanvasCreated(state: NgtState): void {
    // Set initial userData IMMEDIATELY if config is available
    const config = this.configSignal();
    const enableARValue = this.enableAR();  // Read signal input
    if (state.scene) {
      state.scene.userData = {
        ...state.scene.userData, // Preserve existing properties (e.g., orbitControls)
        config,
        enableCameraControls: this.enableCameraControls,
        enableAR: enableARValue,
        // Store signal reference so scene component can track it reactively!
        configSignal: this.configSignal,
        // Pass AppStore to scene components (for Experience app to set prizeRevealed)
        appStore: this.appStore,
        // Provide play() function for scenes that need to auto-trigger (like scratchcard)
        triggerPlay: () => this.play()
      };
    }

    // Set signal to trigger effect for future config changes
    this.canvasStoreSignal.set(state);

    // Firefox fix: NgtsHTMLContent (transform:true) computes a CSS matrix3d from the
    // camera matrix every frame. Firefox collapses its CSS 3D compositing layer when
    // perspective:0px is set on the initial frames (Angular Three's store starts with
    // size={0,0}) and does NOT auto-recover from subsequent valid perspective values —
    // only a change in the CSS matrix3d output triggers Firefox to rebuild the layer.
    //
    // Nudging the camera by a sub-pixel amount on the next rAF causes NgtsHTMLContent
    // to emit a new matrix3d value, which rebuilds the collapsed compositing layer.
    // The second rAF immediately restores the camera — the 0.001 unit offset is
    // imperceptible at normal camera distances and produces no visible jump.
    requestAnimationFrame(() => {
      state.camera.position.x += 0.001;
      state.camera.updateMatrixWorld();
      requestAnimationFrame(() => {
        state.camera.position.x -= 0.001;
        state.camera.updateMatrixWorld();
      });
    });

    // Pass canvas store to renderer so it can access scene.userData (for wheel instance in sceneGraph mode)
    const renderer = this.rendererSignal();
    if (renderer && typeof renderer.setCanvasStore === 'function') {
      renderer.setCanvasStore(state);
      console.log('[GameEngine] Passed canvas store to renderer');
    }

    // Pass AR service to renderer for keepVisible flag during critical moments
    if (renderer && typeof renderer.setArService === 'function' && this.arService) {
      renderer.setArService(this.arService);
      console.log('[GameEngine] Passed AR service to renderer');
    }
  }

  async ngOnInit(): Promise<void> {
    // Optimistically hide the scene when AR mode is enabled.
    // The camera-enabled class (which also hides the scene) is only added once
    // getUserMedia succeeds — leaving a visible flash during permission prompts and
    // camera init. camera-ar-pending closes that gap: applied immediately here,
    // removed as soon as the camera successfully enables (camera-enabled takes over)
    // or as soon as AR fails (permission denied / hardware error) so the scene
    // stays visible as a fallback.
    if (this.enableAR()) {
      this.document.documentElement.classList.add('camera-ar-pending');
    }

    // Get the plugin
    const plugin = this.pluginRegistry.getPlugin(this.gameTypeId);
    if (!plugin) {
      console.error(`[GameEngine] No plugin found for game type: ${this.gameTypeId}`);
      this.gameError.emit({
        code: 'PLUGIN_NOT_FOUND',
        message: `No plugin found for game type: ${this.gameTypeId}`,
        recoverable: false
      });
      return;
    }

    // Create renderer (provides scene component for rendering)
    try {
      const renderer = plugin.createRenderer();
      this.rendererSignal.set(renderer);
      this.setupRendererCallbacks(renderer);

      // Initial renderer config will happen in effect after canvas is ready
      // For scratchcard, Prize API must be called BEFORE updateConfig
      // This is handled in the scratchcard-specific effect above

      // Note: initialized flag, gameReady event, and CTA button setup
      // are now handled in the configuration effect after renderer is ready

      // Setup ResizeObserver to sync host element size changes with Angular Three's store
      this.setupResizeObserver();
    } catch (error) {
      console.error('[GameEngine] Failed to create renderer:', error);
      this.gameError.emit({
        code: 'RENDERER_INIT_FAILED',
        message: 'Failed to initialize game renderer',
        details: error,
        recoverable: false
      });
    }
  }

  /**
   * Setup renderer callbacks to emit component events
   * This connects the renderer's callback-based API to Angular's event system
   * Note: Uses 'any' cast because callbacks are game-specific (wheel has onSpin/onRest, game-board has onRoll/onLand/onWin, etc.)
   *
   * In sceneGraph mode, callbacks don't exist initially on renderer, so we create them.
   * In container mode, callbacks may already exist, so we wrap them.
   */
  private setupRendererCallbacks(renderer: GameRenderer): void {
    const rendererAny = renderer as any;

    // Wheel-specific callbacks (single click gameplay)
    // Connect onSpin callback to gameStart event (wheel-specific)
    const originalOnSpin = rendererAny.onSpin?.bind(renderer);
    rendererAny.onSpin = () => {
      console.log('[GameEngine] Game started');
      // Note: Button already disabled in triggerGameplay() during Prize API call
      this.showCTA.set(false); // Hide button for wheel (one-time spin, no re-enable needed)
      this.gameStart.emit({ timestamp: Date.now() });
      originalOnSpin?.(); // Call original if it existed
    };

    // Connect onRest callback to gameComplete event (wheel-specific)
    const originalOnRest = rendererAny.onRest?.bind(renderer);
    rendererAny.onRest = (index: number) => {
      console.log('[GameEngine] Game complete, result index:', index);
      const config = this.configSignal() as any;
      const resultData = config?.items?.[index];

      this.gameComplete.emit({
        resultIndex: index,
        resultData,
        metadata: {
          timestamp: Date.now(),
          gameType: this.gameTypeId
        }
      });
      originalOnRest?.(index); // Call original if it existed
    };

    // Connect onCurrentIndexChange if needed for progress updates (wheel-specific)
    const originalOnCurrentIndexChange = rendererAny.onCurrentIndexChange?.bind(renderer);
    rendererAny.onCurrentIndexChange = (index: number) => {
      // Could emit gameProgress event here if needed
      originalOnCurrentIndexChange?.(index); // Call original if it existed
    };

    // Game-board-specific callbacks (multiple click gameplay)
    // Connect onRoll callback (button stays visible for multiple rolls)
    const originalOnRoll = rendererAny.onRoll?.bind(renderer);
    rendererAny.onRoll = () => {
      console.log('[GameEngine] Game-board roll started');
      // Note: Button already disabled in triggerGameplay() during Prize API call
      // Don't hide CTA button - game-board needs multiple rolls
      this.gameStart.emit({ timestamp: Date.now() });
      originalOnRoll?.(); // Call original if it existed
    };

    // Connect onLand callback (re-enable button after pawn lands)
    const originalOnLand = rendererAny.onLand?.bind(renderer);
    rendererAny.onLand = (position: number) => {
      console.log('[GameEngine] Game-board pawn landed on position:', position);
      // Re-enable button for next roll (if game not complete, showCTA will stay true)
      this.ctaDisabled.set(false);
      originalOnLand?.(position); // Call original if it existed
    };

    // Connect onWin callback (hide button when game completes)
    const originalOnWin = rendererAny.onWin?.bind(renderer);
    rendererAny.onWin = (prizeId: string) => {
      console.log('[GameEngine] Game-board win on prize:', prizeId);
      // Note: Button already disabled, now hide it completely
      this.showCTA.set(false); // Hide button when game complete

      const config = this.configSignal() as any;
      const prizeIndex = config?.items?.findIndex((item: any) => item.id === prizeId) || 0;
      const resultData = config?.items?.[prizeIndex];

      this.gameComplete.emit({
        resultIndex: prizeIndex,
        resultData,
        metadata: {
          timestamp: Date.now(),
          gameType: this.gameTypeId
        }
      });
      originalOnWin?.(prizeId); // Call original if it existed
    };
  }

  /**
   * Initialize AR when conditions are met
   * This effect runs in Portal's experience preview to enable AR camera
   *
   * CRITICAL: Uses signal-based inputs so effect re-runs when inputs change
   * - enableAR() - tracks AR toggle changes
   * - arMarkerPattern() - tracks pattern URL changes
   * - arOptions() - tracks AR config changes
   * - canvasStoreSignal() - tracks canvas availability
   * - arInitializedSignal() - tracks initialization state
   */
  private initializeAR(): void {
    effect(() => {
      const enableARValue = this.enableAR();  // ✅ Signal - tracked!
      const arMarkerPatternValue = this.arMarkerPattern();  // ✅ Signal - tracked!
      const arOptionsValue = this.arOptions();  // ✅ Signal - tracked!
      const pattern = arMarkerPatternValue || arOptionsValue?.patternUrl;  // Support both APIs
      const canvasStore = this.canvasStoreSignal();  // ✅ Already a signal

      console.log('[GameEngine] AR effect triggered:', {
        enableAR: enableARValue,
        hasPattern: !!pattern,
        hasCanvasStore: !!canvasStore,
        hasArService: !!this.arService,
        // untracked: reading arInitializedSignal here is for logging only — we don't
        // want the effect to re-run every time the signal flips between true/false.
        arInitialized: untracked(() => this.arInitializedSignal())
      });

      // Live scale update: AR is already running and only arOptions changed.
      // Apply the new scale directly without destroying/recreating the component.
      // This prevents multiple overlapping getUserMedia() calls that accumulate
      // when onRefreshPreview() is triggered repeatedly by the slider.
      if (enableARValue && pattern && canvasStore && this.arService && untracked(() => this.arInitializedSignal())) {
        const newScale = arOptionsValue?.sceneScale;
        if (newScale !== undefined) {
          this.arService.updateSceneScale(newScale);
        }
        return;
      }

      // Handle AR disable.
      // IMPORTANT: Do NOT gate this on arInitializedSignal() — there are two races
      // where that check causes the camera to stay on:
      //   1. User toggles off before the Promise.resolve().then() microtask sets
      //      arInitializedSignal to true → initSignal still false → pause() skipped.
      //   2. User toggles off → on → off quickly: initSignal already false (never
      //      reset after first pause) → pause() skipped on second toggle.
      // Always call pause() when AR is turned off; ArService is safe to call
      // idempotently (stopCameraStream() is a no-op when no video element exists).
      // Also reset arInitializedSignal so the init branch can run on re-enable.
      if (!enableARValue && this.arService) {
        console.log('[GameEngine] AR disabled, pausing resources');
        this.arService.pause();
        this.arInitializedSignal.set(false);
        // Re-enable OrbitControls now that AR.js no longer owns the camera
        const orbitControlsOnDisable = this.canvasStoreSignal()?.scene?.userData?.['orbitControls'];
        if (orbitControlsOnDisable && this.enableCameraControls) {
          orbitControlsOnDisable.enabled = true;
          // Reset orbit back to initial position — AR.js may have zeroed or
          // corrupted position/quaternion while matrixAutoUpdate was false.
          orbitControlsOnDisable.reset();
        }
        return;
      }

      // Only initialize if:
      // 1. AR is enabled
      // 2. Pattern URL is provided (via arMarkerPattern or arOptions.patternUrl)
      // 3. Canvas store is ready (scene, camera, renderer available)
      // 4. ArService is available (optional injection)
      // 5. AR not already initialized
      if (enableARValue && pattern && canvasStore && this.arService && !untracked(() => this.arInitializedSignal())) {
        // For Portal: provide callbacks to manage scene visibility
        // Host app (Experience) provides app-specific callbacks (loading state etc.) via
        // ArService.setCallbacks() in its own constructor; game-engine owns all CSS class
        // management so there is a single source of truth for document class toggling.
        this.arService.setCallbacks({
          onSceneVisible: (visible: boolean) => {
            this.document.documentElement.classList.toggle('scene-visible', visible);
          },
          onCameraEnabled: (enabled: boolean) => {
            // Always remove the pending class — camera either started (camera-enabled
            // takes over) or failed (scene should be revealed as a fallback).
            this.document.documentElement.classList.remove('camera-ar-pending');
            this.document.documentElement.classList.toggle('camera-enabled', enabled);
          },
          onUnsupportedOrientation: (unsupported: boolean) => {
            this.document.documentElement.classList.toggle('unsupported-orientation', unsupported);
          }
        });

        // Detect device type from user agent
        const isDesktop = /Windows|Mac|Linux/.test(navigator.userAgent);
        const browser = this.detectBrowser();
        this.arService.setDeviceConfig({
          isDesktop,
          browser,
          // Use provided container or fallback to component's host element
          containerElement: this.arVideoContainer || (this.elementRef.nativeElement as HTMLElement)
        });

        // Initialize AR with pattern and settings
        // Use provided arOptions or defaults
        const arConfig: ArOptions = {
          patternUrl: pattern,
          patternRatio: arOptionsValue?.patternRatio ?? 0.75,
          size: arOptionsValue?.size ?? 1,  // Marker size in meters (larger = smaller scene)
          sceneScale: arOptionsValue?.sceneScale ?? 0.05,  // Scene scale multiplier
          smoothCount: arOptionsValue?.smoothCount ?? 5,
          smoothTolerance: arOptionsValue?.smoothTolerance ?? 0.01,
          smoothThreshold: arOptionsValue?.smoothThreshold ?? 2
        };

        // canvasStore being non-null (guarded above) means Three.js is already ready.
        // Calling initAR() synchronously prevents the microtask race where the user
        // toggles AR off before Promise.resolve().then() fires — which would let
        // initAR() run after pause() has already been called.
        this.arService!.initAR(
          canvasStore.scene,
          canvasStore.camera as any,
          canvasStore.gl,
          arConfig
        );
        // Disable OrbitControls while AR is active — AR.js takes exclusive
        // ownership of the Three.js camera matrix on every frame; if OrbitControls
        // is still enabled it fights AR.js and produces a broken jittery view.
        const orbitControlsOnEnable = canvasStore.scene?.userData?.['orbitControls'];
        if (orbitControlsOnEnable) {
          orbitControlsOnEnable.enabled = false;
        }
        this.arInitializedSignal.set(true);
        console.log('[GameEngine] AR initialized successfully');
      }
    });
  }

  /**
   * Setup ResizeObserver to sync host element size with Angular Three store
   * Calls store.setSize() which automatically:
   * - Updates camera aspect ratio and projection matrix
   * - Resizes WebGL renderer
   * - Triggers NgtsHTML component recalculation
   */
  private setupResizeObserver(): void {
    const hostElement = this.elementRef.nativeElement as HTMLElement;
    let rafId: number | null = null;

    this.resizeObserver = new ResizeObserver((entries) => {
      // Cancel pending RAF to debounce rapid resize events
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(() => {
        const entry = entries[0];
        if (!entry) return;

        const { width, height } = entry.contentRect;
        if (width <= 0 || height <= 0) return;

        const canvasStore = this.canvasStoreSignal();
        if (!canvasStore) return;

        // Get absolute position for HTML overlay calculations
        const rect = hostElement.getBoundingClientRect();

        // Update Angular Three's store - triggers automatic camera/renderer updates
        canvasStore.setSize(width, height, rect.top, rect.left);
      });
    });

    this.resizeObserver.observe(hostElement);
  }

  /**
   * Detect browser type for AR device config
   */
  private detectBrowser(): 'Firefox' | 'Chrome' | 'Safari' | 'Edge' | 'Unknown' {
    const ua = navigator.userAgent;
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Edg')) return 'Edge';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Safari')) return 'Safari';
    return 'Unknown';
  }

  /**
   * Trigger gameplay when CTA button is clicked
   * For multi-roll games (game-board), this may be called multiple times
   */
  protected async triggerGameplay(): Promise<void> {
    // Disable button immediately to prevent double-clicks during Prize API call
    this.ctaDisabled.set(true);

    try {
      const result = await this.play();

      // For multi-turn games (poll, game-board), result is null until game completes.
      // Re-enable the button so the user can submit the next answer / roll.
      // Wheel and scratchcard manage their own CTA visibility via onSpin/onRest callbacks
      // so they don't need this path.
      if (result === null) {
        this.ctaDisabled.set(false);
      }

      // Game complete — hide the button (game-board uses its onWin callback for this,
      // but games without a dedicated callback, e.g. poll, rely on this path).
      if (result !== null) {
        this.showCTA.set(false);
        console.log('[GameEngine] Game complete with result:', result);

        // Emit gameComplete for game types that don't have dedicated completion callbacks.
        // Wheel emits via its onRest callback; game-board emits via its onWin callback.
        // All other game types (poll, dice-roll, scratchcard) use this path.
        if (this.gameTypeId !== 'wheel' && this.gameTypeId !== 'game-board') {
          this.gameComplete.emit({
            resultIndex: (result as GameResult).prize
              ? ((this.configSignal() as any)?.items?.findIndex(
                  (item: any) => item.id === (result as GameResult).prize?.id,
                ) ?? 0)
              : -1,
            resultData: result,
            metadata: { timestamp: Date.now(), gameType: this.gameTypeId },
          });
        }
      }
    } catch (error) {
      console.error('[GameEngine] Gameplay error:', error);
      this.gameError.emit({
        code: 'GAMEPLAY_ERROR',
        message: 'An error occurred during gameplay',
        details: error,
        recoverable: true
      });
    }
  }

  ngOnDestroy(): void {
    console.log('[GameEngine] Destroying component and cleaning up resources');

    // Clean up ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    // Always call destroy() on the AR service — do NOT gate on arInitializedSignal.
    //
    // Why: pause() (called on AR toggle-off) resets arInitializedSignal to false, but
    // does NOT clear lastInitParams or remove document/window event listeners — those
    // are only cleaned up in destroy().  If the component is subsequently recreated by
    // onRefreshPreview() (e.g. triggered by the scene-scale slider debounce), the stale
    // lastInitParams can cause the camera to restart via onVisibilityChange().
    //
    // ArService.destroy() is safe to call unconditionally: it increments the generation
    // counter first (which invalidates any in-flight getUserMedia callbacks), then
    // tears down whatever partial state remains.
    if (this.arService) {
      console.log('[GameEngine] Cleaning up AR resources');
      this.arService.destroy();
    }
    this.arInitializedSignal.set(false);
    // ArService.destroy() already removes these classes, but be explicit here
    // in case ArService was not injected (optional injection).
    this.document.documentElement.classList.remove('camera-enabled');
    this.document.documentElement.classList.remove('camera-ar-pending');
    this.document.documentElement.classList.remove('scene-visible');

    // Angular Three automatically handles WebGL context cleanup
    // No manual disposal needed

    // Clean up game renderer
    const renderer = this.rendererSignal();
    renderer?.destroy();
    this.rendererSignal.set(null);
    this.canvasStoreSignal.set(null);
    this.initialized = false;
  }

  /**
   * Get OrbitControls instance (for ExperiencePreview to manipulate)
   * Reads from scene.userData where scene components store the controls
   */
  public getOrbitControls(): OrbitControls | null {
    const canvasStore = this.canvasStoreSignal();
    const controls = canvasStore?.scene?.userData['orbitControls'] ?? null;
    return controls;
  }

  // ============================================================================
  // Camera Control Methods (deprecated - ExperiencePreview manipulates OrbitControls directly)
  // Kept for backward compatibility but not actively used
  // ============================================================================

  // ============================================================================
  // Game Actions
  // ============================================================================

  /**
   * Update 3D mesh materials in scene when config changes.
   * Traverses the scene to find cylinder and arrow meshes and updates their materials.
   */
  private updateSceneMaterials(scene: THREE.Scene, config: unknown): void {
    const settings = (config as any)?.settings;
    if (!settings) return;

    scene.traverse((object) => {
      if (object instanceof THREE.Mesh && object.material instanceof THREE.MeshStandardMaterial) {
        // Update cylinder material (identified by rotation)
        if (object.rotation.x === Math.PI / 2) {
          if (settings.cylinderColor) object.material.color.set(settings.cylinderColor);
          if (settings.cylinderOpacity !== undefined) object.material.opacity = settings.cylinderOpacity;
          if (settings.cylinderRoughness !== undefined) object.material.roughness = settings.cylinderRoughness;
          if (settings.cylinderMetalness !== undefined) object.material.metalness = settings.cylinderMetalness;
          object.material.needsUpdate = true;
        }
        // Update arrow material (identified by rotation)
        else if (object.rotation.z === Math.PI && settings.arrowColor) {
          object.material.color.set(settings.arrowColor);
          object.material.needsUpdate = true;
        }
      }
    });
  }

  /**
   * Trigger game play (e.g., spin wheel, reveal scratchcard, roll die).
   * Passes platform-provided getPrizeIndex callback to renderer.
   * Returns a promise that resolves with the game result.
   *
   * For multi-roll games (game-board), returns null until game completes,
   * allowing the CTA button to be clicked multiple times.
   */
  async play(): Promise<GameResult | null> {
    const renderer = this.rendererSignal();
    if (!renderer) {
      throw new Error('Renderer not initialized');
    }

    // Pass platform callback to renderer for Prize API access
    const result = await renderer.play({ getPrizeIndex: this.getPrizeIndex });

    // For multi-roll games (game-board), result may be null (not complete)
    // In this case, button stays visible for next roll
    // For single-action games (wheel, scratchcard), result is always returned

    return result;
  }

  /**
   * Get a screenshot of the current game state.
   * Returns a promise that resolves with a data URL.
   */
  async getScreenshot(): Promise<string> {
    const renderer = this.rendererSignal();
    if (!renderer?.getScreenshot) {
      throw new Error('Screenshot not supported by this game type');
    }
    return renderer.getScreenshot();
  }

  /**
   * Reset the game to its initial state for another play in preview mode.
   *
   * Only callable when `previewMode = true`. No-op otherwise.
   *
   * Does NOT destroy this component, call the Prize API, make network requests,
   * or touch the WebGL / AR camera context. Each renderer re-randomises its own
   * local state (prizes, animations, positions) independently.
   *
   * Used by ExperiencePreviewComponent.resetPreview() to avoid the null/restore
   * key dance that races with in-flight getUserMedia() calls when AR is active.
   */
  async resetGame(): Promise<void> {
    if (!this.previewMode) return;

    const renderer = this.rendererSignal();
    if (!renderer?.reset) return;

    // Restore CTA button to its initial visible/enabled state
    this.ctaDisabled.set(false);
    this.ctaLabel.set(null); // Clear any dynamic label override
    if (this.ctaButton?.enabled) {
      this.showCTA.set(true);
    }

    await renderer.reset();
  }}